import React from 'react'
import GhostButton from '../../Buttons/GhostButton'
import { ReactComponent as PlusIcon } from '../../../images/icons/plus.svg'
import { useFormContext } from 'react-hook-form'
import { Vendor } from '../../../lib/types/Vendor'
import { getImageURL } from '../../../lib/utils/getImageURL'
import { ReactComponent as LinkIcon } from '../../../images/icons/connect_link_icon.svg'
import { useModal } from '../../useModal'
import { Button } from '@demandlocal/components'

type Props = {
  vendor: Vendor
  disabled?: boolean
}

export const TekionInstructions = (): JSX.Element => {
  const { closeModal } = useModal()

  return (
    <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full overflow-auto'}>
      <div className={'flex flex-1 flex-col overflow-auto gap-4'}>
        <span className={'font-bold text-blue-primary pl-12 text-3xl'}>
          Connecting to Tekion
        </span>
        <div className="flex flex-col px-12 gap-5">
          <span className="font-bold">Demand Local Internal Documentation Only</span>
          <ol className="*:pt-5">
            <li>
              1. Get dealer to sign the Authorizations form.{' '}
              <ul className="list-disc list-inside">
                <li>Download a copy and Change the File name to dealer name</li>
                <li>
                  Update the Frequency to daily and start date of the month they sign up
                </li>
                <li>Have the dealership sign the form before sending to Tekion</li>
              </ul>
              <br />
              <span>
                For regular bulk history requests use this{' '}
                <a
                  className="font-bold"
                  href="https://file.notion.so/f/f/dcfc2468-14e5-4660-b6e0-113d4e4fd510/55474fbc-8290-4d3c-b22f-592e6672dbaf/Tekion-Vendor-Disclosure-Authorization_Form_Template_(January_2024)_copy.pdf?table=block&id=dc77a6e7-aa42-4112-a0d7-9e2535ef606f&spaceId=dcfc2468-14e5-4660-b6e0-113d4e4fd510&expirationTimestamp=1746057600000&signature=I_zfKegK-ezCDRHFuCY43ZluOhh7Ze8AdwA1WlGHvGo&downloadName=Tekion-Vendor-Disclosure-Authorization+Form_Template+%28January+2024%29+copy.pdf"
                  target="_blank"
                  rel="noreferrer">
                  form
                </a>
              </span>
              <br />
              <span>
                For transferring/standard API connection requests ie from Dealervault to
                Tekion please us this{' '}
                <a
                  className="font-bold"
                  href="https://file.notion.so/f/f/dcfc2468-14e5-4660-b6e0-113d4e4fd510/48f10962-f883-4ebc-9810-93a1cf287c56/Tekion-Vendor-Disclosure-Instructions.pdf?table=block&id=1deab6f3-cc62-80b7-95a2-e7286de4cea2&spaceId=dcfc2468-14e5-4660-b6e0-113d4e4fd510&expirationTimestamp=1746057600000&signature=P8YBmpw1YMrz9VKQQWeh6tOajZvkNbjNYln9cOKIUHk&downloadName=Tekion-Vendor-Disclosure-Instructions.pdf"
                  target="_blank"
                  rel="noreferrer">
                  form
                </a>
              </span>
            </li>
            <li>2. Send send file to Tekion Integrations support</li>
            <li>3. Demand Local will get notified when it comes in</li>
            <li>
              4. In LinkOne Admin go to integrations tab on the dealer and type in the
              Dealer Id.
            </li>
            <li>
              5. As a user, create the source. Make sure to pull one for sales and one for
              service.
            </li>
          </ol>
          <span>
            To request historical dealership data from Tekion we need to do the following
            steps:
          </span>
          <ol>
            <li>
              1. Download a copy of the{' '}
              <a
                href="https://file.notion.so/f/f/dcfc2468-14e5-4660-b6e0-113d4e4fd510/095d8fac-9355-4420-852e-0d81a87eb07b/TEKION_Bulk_data_extract_request_form_updated_(1)_(1).pdf?table=block&id=4b24ab65-f18e-40a7-abac-b44ff54c8bad&spaceId=dcfc2468-14e5-4660-b6e0-113d4e4fd510&expirationTimestamp=1745971200000&signature=e9k5iq4SJBtlIH2l4Taq4iCbLy6Dy4h98QpqOb1LyIQ&downloadName=TEKION_Bulk_data_extract_request_form_updated+%281%29+%281%29.pdf"
                target="_blank"
                className="font-bold"
                rel="noreferrer">
                bulk data extract request form
              </a>{' '}
              and change the file name to dealer name.
            </li>
            <li>2. Fill out Dealership name.</li>
            <li>
              3. Can you please assist and activate “Dealer Name” per the Bulk Data
              Extract Form
            </li>
            <li>4. Send file to Tekion Integrations support.</li>
            <li>
              5. Get the API information from the Tekion and send to development to set up
              FTP.
              <ul className="list-disc list-inside">
                <li>integrationsuppoer@tekion.com</li>
                <li>CC - brian@demandlocal.com</li>
              </ul>
            </li>
          </ol>
        </div>
      </div>
      <div className={'h-20 items-center flex w-full justify-start border-t-2 py-4 mt-4'}>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.preventDefault()
            closeModal()
          }}>
          Close
        </Button>
      </div>
    </div>
  )
}

const ConnectedSourceTekion = ({ vendor, disabled }: Props): JSX.Element => {
  const { setValue } = useFormContext()
  const { handleModal } = useModal()

  return (
    <div key={vendor.id} className={'flex items-center justify-between'}>
      <div className={'flex items-center gap-3 flex-1'}>
        <div className={'flex items-center justify-center'}>
          <div className={'w-8 h-8 rounded-full flex'}>
            <img
              src={getImageURL(`images/vendors/${vendor.logo}`)}
              className={'object-contain'}
            />
          </div>
        </div>
        <span
          className={`font-bold text-base text-blue-primary flex-1 ${
            (disabled || !vendor.connected) && 'opacity-50'
          }`}>
          {vendor.name}
        </span>

        {!disabled && (
          <GhostButton
            onClick={(e) => {
              e.preventDefault()
              if (vendor.connected) setValue?.(`vendor_${vendor.id}`, [{}])
            }}>
            {vendor.connected ? (
              <PlusIcon className={'w-4 h-6 stroke-primary-persian-blue '} />
            ) : (
              <LinkIcon
                stroke={'#0932C2'}
                fill={'#0932C2'}
                className={'w-4 h-6'}
                onClick={() => handleModal(<TekionInstructions />)}
              />
            )}
          </GhostButton>
        )}
      </div>
    </div>
  )
}

export default ConnectedSourceTekion
